import type { SingleDayHours } from 'core/types/lenderConfig'

// Importing remeda with relative path to avoid circular dependency
import * as R from './remeda'

type DaySchema = {
  code: string
  aliases: Array<string>
}

/** Mon → Sun; order defines index used in ranges and output. */
const DAYS_SCHEMA: Array<DaySchema> = [
  { code: 'MON', aliases: ['monday', 'mon'] },
  { code: 'TUE', aliases: ['tuesday', 'tue', 'tues'] },
  { code: 'WED', aliases: ['wednesday', 'wed', 'weds'] },
  { code: 'THU', aliases: ['thursday', 'thu', 'thur', 'thurs'] },
  { code: 'FRI', aliases: ['friday', 'fri'] },
  { code: 'SAT', aliases: ['saturday', 'sat'] },
  { code: 'SUN', aliases: ['sunday', 'sun'] },
]

const WEEK_DAY_COUNT = DAYS_SCHEMA.length

const ALIAS_LOOKUP: Array<{ alias: string; idx: number }> = R.pipe(
  DAYS_SCHEMA,
  R.map.indexed((d, idx) => R.map(d.aliases, (alias) => ({ alias, idx }))),
  (rows) => R.flatMap(rows, (x) => x),
  (rows) => [...rows].sort((a, b) => b.alias.length - a.alias.length),
)

const normalizeTimeToken = (time: string): string => {
  const [hours, minutes = '00'] = time.split(':')
  return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`
}

const parseSingleDayToken = (token: string): number | undefined => {
  const t = token.trim().toLowerCase().replace(/\.$/, '')
  if (!t) return undefined

  const exact = R.find(ALIAS_LOOKUP, ({ alias }) => alias === t)
  if (exact) return exact.idx

  const prefix = R.find(ALIAS_LOOKUP, ({ alias }) => alias.length >= 3 && t.startsWith(alias))
  if (prefix) return prefix.idx

  return R.find(ALIAS_LOOKUP, ({ alias }) => alias.length >= 3 && alias.startsWith(t))?.idx
}

const parseDayIndicesFromPart = (dayPart: string): Array<number> | undefined => {
  const trimmed = dayPart.trim()
  if (!trimmed) return undefined

  const parts = trimmed
    .split(/\s*-\s*/)
    .map((p) => p.trim())
    .filter(Boolean)
  if (parts.length === 1) {
    const idx = parseSingleDayToken(parts[0])
    return idx === undefined ? undefined : [idx]
  }
  if (parts.length === 2) {
    const start = parseSingleDayToken(parts[0])
    const end = parseSingleDayToken(parts[1])
    if (start === undefined || end === undefined) return undefined
    if (start <= end) return R.range(start, end + 1)
    // Week wrap (e.g. Fri–Tue → Fri, Sat, Sun, Mon, Tue)
    return [...R.range(start, WEEK_DAY_COUNT), ...R.range(0, end + 1)]
  }
  return undefined
}

const TIME_RANGE_RE = /(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/gi

const extractTimeRanges = (clause: string): Array<[string, string]> => {
  const ranges: Array<[string, string]> = []
  let m: RegExpExecArray | null
  const re = new RegExp(TIME_RANGE_RE.source, TIME_RANGE_RE.flags)
  while ((m = re.exec(clause)) !== null) {
    ranges.push([normalizeTimeToken(m[1]), normalizeTimeToken(m[2])])
  }
  return ranges
}

const is24HoursClause = (clause: string): boolean =>
  /\b(?:open\s+)?24\s*(?:hours?|hrs?)\b/i.test(clause) || /\bopen\s+24\b/i.test(clause)

const isClosedClause = (clause: string): boolean => /\bclosed\b/i.test(clause)

type ClausePayload = {
  hoursList?: Array<[string, string]>
  closed?: boolean
  '24hours'?: boolean
}

const parseClause = (clause: string): { dayIndices: Array<number>; payload: ClausePayload } | undefined => {
  const trimmed = clause.trim()
  if (!trimmed) return undefined

  const ranges = extractTimeRanges(trimmed)
  const firstTime = trimmed.search(/\d{1,2}:\d{2}/)
  let dayPart: string
  if (firstTime >= 0) {
    dayPart = trimmed.slice(0, firstTime).trim()
  } else {
    dayPart = trimmed
      .replace(/\bclosed\b/gi, ' ')
      .replace(/\b(?:open\s+)?24\s*(?:hours?|hrs?)\b/gi, ' ')
      .replace(/\bopen\s+24\b/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  }

  const dayIndices = parseDayIndicesFromPart(dayPart)
  if (!dayIndices || dayIndices.length === 0) return undefined

  if (ranges.length > 0) {
    return { dayIndices, payload: { hoursList: ranges } }
  }
  if (isClosedClause(trimmed)) {
    return { dayIndices, payload: { closed: true } }
  }
  if (is24HoursClause(trimmed)) {
    return { dayIndices, payload: { '24hours': true } }
  }
  return undefined
}

/**
 * Parses API string `businessHours.normal` (e.g. "Mon-Fri 9:00-17:00; Sat 10:00-14:00; Sun Closed")
 * into the structured week array the app expects (MON–SUN, {@link SingleDayHours}).
 * Days not mentioned default to `{ closed: true }`.
 */
export const parseBusinessHoursNormalString = (value: string): Array<SingleDayHours> | undefined => {
  const trimmed = value.trim()
  if (!trimmed) return undefined

  const clauses = R.pipe(
    trimmed.split(';'),
    R.map((c) => c.trim()),
    R.filter((c) => c.length > 0),
  )

  const byIdx: Array<SingleDayHours | undefined> = Array.from({ length: WEEK_DAY_COUNT }, () => undefined)
  let anyApplied = false

  for (const clause of clauses) {
    const parsed = parseClause(clause)
    if (!parsed) continue
    anyApplied = true
    const { dayIndices, payload } = parsed
    for (const idx of dayIndices) {
      const code = DAYS_SCHEMA[idx]?.code
      if (!code) continue
      byIdx[idx] = { day: code, ...payload }
    }
  }

  if (!anyApplied) return undefined

  return DAYS_SCHEMA.map(({ code }, idx) => {
    const existing = byIdx[idx]
    if (existing) return existing
    return { day: code, closed: true }
  })
}

export const getBusinessHoursNormalAsArray = (
  normal: Array<SingleDayHours> | string | undefined,
): Array<SingleDayHours> | undefined => {
  if (normal === undefined) return undefined
  if (Array.isArray(normal)) return normal
  return parseBusinessHoursNormalString(normal)
}
